import React, { useState } from 'react';
import { dateLabel, sendAdminNotification } from '../lib/compat.js';

function FieldLabel({ children, required }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
      {children}{required && <span style={{ color: '#B91C1C', marginLeft: 2 }}>*</span>}
    </span>
  );
}

export default function AdminNotifications({ data, onRefresh }) {
  const [form, setForm] = useState({ audience: 'all', bookTitle: '', district: '', title: '', body: '' });
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  const districts = [...new Set(data.students.map((s) => s.district).filter(Boolean))].sort();
  const bookTitles = [...new Set(data.books.map((b) => b.title).filter(Boolean))].sort();

  const targetCount = (() => {
    if (form.audience === 'all') return data.students.length;
    if (form.audience === 'district' && form.district)
      return data.students.filter((s) => s.district === form.district).length;
    if (form.audience === 'book' && form.bookTitle) {
      const buyers = new Set();
      data.orders.forEach((o) => {
        const has = (o.items || []).some((i) => [i.bt, i.bookTitle].join(' ').toLowerCase().includes(form.bookTitle.toLowerCase()));
        if (has) buyers.add((o.delivery?.phone || o.studentPhone || '').replace(/\D/g, '').slice(-10));
      });
      return buyers.size;
    }
    return 0;
  })();

  async function send() {
    if (!form.title.trim() || !form.body.trim()) { setMsg('⚠️ Title and message are required.'); return; }
    setSending(true);
    setMsg('');
    try {
      const count = await sendAdminNotification({ ...form, students: data.students, orders: data.orders });
      setMsg(`✅ Notification sent to ${count} student(s).`);
      setForm({ audience: 'all', bookTitle: '', district: '', title: '', body: '' });
      await onRefresh();
    } catch (e) {
      setMsg('❌ ' + (e.message || 'Unable to send.'));
    } finally {
      setSending(false);
    }
  }

  const broadcasts = (data.notifications || []).filter((n) => n.type === 'admin_broadcast' || n.audience);
  const uniqueBroadcasts = broadcasts.reduce((acc, n) => {
    const key = (n.createdAt || '') + (n.title || '');
    if (!acc.seen.has(key)) { acc.seen.add(key); acc.items.push(n); }
    return acc;
  }, { seen: new Set(), items: [] }).items.slice(0, 20);

  const selectStyle = { width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
  const inputStyle = { width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' };

  return (
    <div className="adminTwoCol">
      {/* Send form */}
      <div className="adminCard">
        <div className="adminCardHead"><h3>📢 Send Push Notification</h3></div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {msg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: msg.startsWith('✅') ? '#e8f5ee' : msg.startsWith('⚠️') || msg.startsWith('❌') ? '#FECDD3' : '#EEF0FF', color: msg.startsWith('✅') ? '#1a7a3e' : msg.startsWith('⚠️') || msg.startsWith('❌') ? '#9B1C1C' : 'var(--admin-navy)' }}>
              {msg}
            </div>
          )}

          <div>
            <FieldLabel>Send To</FieldLabel>
            <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value, bookTitle: '', district: '' })} style={selectStyle}>
              <option value="all">🌍 All Students ({data.students.length})</option>
              <option value="book">📚 Students who bought a specific book</option>
              <option value="district">📍 Students from a specific district</option>
            </select>
          </div>

          {form.audience === 'book' && (
            <div>
              <FieldLabel>Select Book</FieldLabel>
              <select value={form.bookTitle} onChange={(e) => setForm({ ...form, bookTitle: e.target.value })} style={selectStyle}>
                <option value="">Select book…</option>
                {bookTitles.map((t) => <option value={t} key={t}>{t}</option>)}
              </select>
            </div>
          )}

          {form.audience === 'district' && (
            <div>
              <FieldLabel>Select District</FieldLabel>
              <select value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} style={selectStyle}>
                <option value="">Select district…</option>
                {districts.map((d) => <option value={d} key={d}>{d}</option>)}
              </select>
            </div>
          )}

          {/* Target count */}
          {targetCount > 0 && (
            <div style={{ background: '#EEF0FF', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--admin-navy)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>👥</span>
              This notification will be sent to <strong style={{ marginLeft: 4 }}>{targetCount} student{targetCount !== 1 ? 's' : ''}</strong>.
            </div>
          )}

          <div>
            <FieldLabel required>Notification Title</FieldLabel>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. 📦 RRB Combo Books Back in Stock!"
              style={inputStyle}
            />
          </div>

          <div>
            <FieldLabel required>Message</FieldLabel>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="e.g. Great news! RRB Group D Combo Books are now back in stock. Order now before they sell out!"
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
            />
          </div>

          {/* Preview Card */}
          {(form.title || form.body) && (
            <div style={{ background: 'var(--admin-cream)', borderRadius: 10, padding: 14, border: '1px solid var(--admin-border)' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--admin-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.07em' }}>Preview</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 44, height: 44, background: 'var(--admin-navy)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>📚</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--admin-navy)' }}>{form.title || 'Notification Title'}</div>
                  <div style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 4, lineHeight: 1.5 }}>{form.body || 'Your message here…'}</div>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            onClick={send}
            disabled={sending || !form.title || !form.body}
            style={{ padding: '13px', justifyContent: 'center', fontSize: 14, fontWeight: 700, borderRadius: 10, opacity: (!form.title || !form.body) ? 0.5 : 1 }}
          >
            📢 {sending ? 'Sending…' : 'Send Notification'}
          </button>
        </div>
      </div>

      {/* Sent history */}
      <div className="adminCard" style={{ height: 'fit-content' }}>
        <div className="adminCardHead"><h3>📋 Sent History</h3><span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{uniqueBroadcasts.length} sent</span></div>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          {uniqueBroadcasts.length ? uniqueBroadcasts.map((item, i) => (
            <div key={item.id || i} style={{ padding: '12px 18px', borderBottom: '1px solid var(--admin-border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <strong style={{ fontSize: 13, color: 'var(--admin-text)', lineHeight: 1.3 }}>{item.title}</strong>
                <span style={{ fontSize: 10, color: 'var(--admin-muted)', flexShrink: 0, marginTop: 2 }}>{dateLabel(item.createdAt)}</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--admin-muted)', margin: '0 0 6px', lineHeight: 1.4 }}>{item.body}</p>
              <span className="badge" style={{ fontSize: 10, background: '#EEF0FF', color: 'var(--admin-navy)' }}>
                {item.audience === 'all' ? '🌍 All Students' : item.audience === 'district' ? `📍 ${item.district}` : `📚 ${item.bookTitle}`}
              </span>
            </div>
          )) : (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--admin-muted)', fontSize: 13 }}>
              No notifications sent yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
